import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import {
  Box,
  Flex,
  Button,
  Paragraph,
  useNotification,
  LoadingContainer,
  usePaginationReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import { useAuth } from '@sopranodesign/pulse-auth';

import {
  fetchJiraProjects,
  getJiraSettings
} from '../api';
import JiraProjectsList from './JiraProjectsList';
import JiraIssuesList from './JiraIssuesList';
import JiraModalManager from './JiraModalManager';

const Jira = () => {
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission('edit generative ai resource');
  const location = useLocation();
  const history = useHistory();
  const { showSuccess, showError } = useNotification();

  const pagination = usePaginationReducer({
    pathname: '/jira',
    fetchQueryKey: ['generative-ai/jira']
  });

  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isViewingIssues, setIsViewingIssues] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [isAddProjectsLoading, setIsAddProjectsLoading] = useState(false);
  const [projectLoadingIds, setProjectLoadingIds] = useState({});
  const [issueLoadingIds, setIssueLoadingIds] = useState({});
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [errorIds, setErrorIds] = useState({});
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['generative-ai/jira/', pagination.params],
    queryFn: () => getJiraSettings(pagination.params),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const responseData = useMemo(() => data?.data ?? {}, [data]);
  const settings = useMemo(() => responseData.settings ?? null, [responseData]);
  const paginatedProjects = useMemo(() => responseData.projects ?? [], [responseData]);
  const meta = useMemo(() => responseData.meta ?? [], [responseData]);
  const hasPending = useMemo(() => responseData.has_pending ?? false, [responseData]);

  useEffect(() => {
    const next = !isViewingIssues && (settings?.status === 'pending_disconnect' || hasPending);
    setShouldRefetch(next);
  }, [hasPending, settings?.status, isViewingIssues, paginatedProjects]);

  // The OAuth popup navigates itself to this same URL after the Atlassian
  // callback redirects it here — the opener tab is untouched, so this only
  // fires in whichever tab actually completes the flow.
  useEffect(() => {
    const oauthStatus = new URLSearchParams(location.search).get('oauth');

    if (!oauthStatus) { return; }

    if (oauthStatus === 'connected') {
      showSuccess('Connected to Jira via OAuth2');
      queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
    } else if (oauthStatus === 'denied') {
      showError('OAuth2 connection was not completed');
    } else if (oauthStatus === 'no_accessible_site') {
      showError('No accessible Jira site was found for that Atlassian account');
    }

    history.replace({ search: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  useEffect(() => {
    if (!isViewingIssues) {
      setIssueLoadingIds({});
    }
  }, [isViewingIssues]);

  const handleAddProjects = async () => {
    setIsAddProjectsLoading(true);
    try {
      const response = await fetchJiraProjects();
      setAvailableProjects(response.data ?? []);
      openModal('projects');
    } finally {
      setIsAddProjectsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading Jira...</Paragraph>
      </Flex>
    );
  }

  return (
    <Flex col>
      {/* Initial connection UI */}
      {!settings && !isLoading && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Connect your Jira account to sync issues.</Paragraph>
            {canEditResource && (
              <Button
                icon="plus"
                variant="primary"
                onClick={() => openModal('connect')}
              >
                Connect Jira
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Awaiting connection UI - shown when a previous connection attempt (e.g. a
          denied consent) never completed, so a settings row exists but isn't connected */}
      {settings && settings.status === 'awaiting_connection' && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>The previous connection attempt didn&apos;t complete. Try connecting again.</Paragraph>
            {canEditResource && (
              <Button
                icon="plus"
                variant="primary"
                onClick={() => openModal('connect')}
              >
                Connect Jira
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Project selection UI */}
      {settings && settings.status === 'connected' && paginatedProjects.length === 0 && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Choose Jira Projects to Sync.</Paragraph>
            <Flex center wrap="true" gap="2">
              <Button
                icon="plus"
                variant="secondary"
                loading={isAddProjectsLoading}
                onClick={handleAddProjects}
              >
                Select Projects
              </Button>
              {canEditResource && (
                <Button
                  icon="trash"
                  variant="secondary"
                  colour="danger"
                  onClick={() => openModal('confirmation', { type: 'disconnect' })}
                >
                  Disconnect
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Reauthorization required UI */}
      {settings && settings.status === 'reauth_required' && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Jira needs to be reconnected — the connection has expired.</Paragraph>
            {canEditResource && (
              <Button
                icon="refresh"
                variant="primary"
                onClick={() => openModal('connect')}
              >
                Reconnect Jira
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Disconnecting UI */}
      {settings && settings.status === 'pending_disconnect' && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Disconnecting your Jira account. This may take a moment...</Paragraph>
          </Flex>
        </Box>
      )}

      {/* Projects List */}
      {settings && settings.status === 'connected' && paginatedProjects.length > 0 && (
        <JiraProjectsList
          projects={paginatedProjects}
          settings={settings}
          loadingIds={projectLoadingIds}
          isSyncing={isSyncingAll}
          onDisconnect={() => openModal('confirmation', { type: 'disconnect' })}
          onView={(id) => {
            setSelectedProjectId(id);
            setIsViewingIssues(true);
          }}
          onResync={(project) => openModal('confirmation', { type: 'resync', project })}
          onDelete={(project) => openModal('confirmation', { type: 'delete', project })}
          onAddProjects={handleAddProjects}
          onResyncAll={() => openModal('confirmation', { type: 'resyncAll' })}
          isAddProjectsLoading={isAddProjectsLoading}
          pagination={{ meta, props: pagination.props }}
        />
      )}

      {/* Issues List */}
      {selectedProjectId && (
        <JiraIssuesList
          projectId={selectedProjectId}
          handleHide={() => {
            setSelectedProjectId(null);
            setIsViewingIssues(false);
            queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          }}
          onOpenTagsModal={(issue) => openModal('tags', { issue, projectId: selectedProjectId })}
          onOpenRecrawlModal={(issue) =>
            openModal('confirmation', { type: 'recrawl', issue, projectId: selectedProjectId })
          }
          loadingIds={issueLoadingIds}
          errorIds={errorIds}
        />
      )}

      {/* Modal Manager */}
      <JiraModalManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        projects={availableProjects}
        setErrorIds={setErrorIds}
        setProjectLoadingIds={setProjectLoadingIds}
        setIssueLoadingIds={setIssueLoadingIds}
        setIsSyncingAll={setIsSyncingAll}
      />
    </Flex>
  );
};

export default Jira;
